import type { ReactNode } from "react";
import { formatInteger } from "../../lib/i18n/format";
import { TipLayer, TipRow, useTip } from "./tooltip";

export interface StackedSegment {
	key: string;
	value: number;
	color: string;
	label: string;
}

export interface StackedRow {
	key: string;
	/** The row's name — text, or a link to the row's own page. */
	label: ReactNode;
	/** The tooltip's heading (plain text). */
	title: string;
	segments: StackedSegment[];
	/** Shown at the bar's tip; defaults to the sum of the segments. */
	total?: ReactNode;
	/** A muted line under the label. */
	note?: ReactNode;
}

/**
 * Horizontal stacked bars in HTML / CSS (crisp text, no SVG scaling): one row
 * per item, segments in the order given, a 2px surface gap between them, the
 * data end rounded 4px and the baseline square, the total at the tip.
 *
 *   count   every bar on one scale — the longest total fills the track
 *   share   every bar is 100% — the mix, not the size
 *
 * Hover a bar for its numbers; the frame's table view carries them too.
 */
export function StackedBars({ rows, mode = "count", labelWidth = "11rem" }: { rows: readonly StackedRow[]; mode?: "count" | "share"; labelWidth?: string }) {
	const tip = useTip();
	const max = Math.max(1, ...rows.map((r) => r.segments.reduce((n, s) => n + Math.max(0, s.value), 0)));

	return (
		<>
			<ul className="flex flex-col gap-[0.5rem]">
				{rows.map((r) => {
					const sum = r.segments.reduce((n, s) => n + Math.max(0, s.value), 0);
					const scale = mode === "share" ? (sum > 0 ? sum : 1) : max;
					const visible = r.segments.filter((s) => s.value > 0);
					const content = (
						<div className="flex min-w-[11rem] flex-col gap-[0.125rem]">
							<p className="mb-[0.25rem] font-bold">{r.title}</p>
							{r.segments.map((s) => (
								<TipRow key={s.key} color={s.color} label={s.label} value={formatInteger(s.value)} />
							))}
						</div>
					);
					return (
						<li key={r.key} className="grid items-center gap-x-[0.75rem] gap-y-[0.25rem] sm:grid-cols-[var(--lw)_minmax(0,1fr)]" style={{ "--lw": labelWidth } as React.CSSProperties}>
							<div className="flex min-w-0 flex-col">
								<span className="truncate text-[0.875rem] text-ink">{r.label}</span>
								{r.note && <span className="truncate text-[0.75rem] text-muted">{r.note}</span>}
							</div>
							<div className="flex min-w-0 items-center gap-[0.5rem]">
								<div
									className="flex h-[0.875rem] min-w-0 flex-1 items-stretch"
									onPointerMove={(e) => tip.at(e, content)}
									onPointerLeave={tip.hide}
									aria-hidden="true"
								>
									{visible.length === 0 ? (
										<span className="h-full w-[2px] rounded-[1px] bg-rule" />
									) : (
										<span className="flex h-full gap-[2px]" style={{ width: `${(sum / scale) * 100}%` }}>
											{visible.map((s, i) => (
												<span
													key={s.key}
													className={`h-full min-w-[2px] ${i === visible.length - 1 ? "rounded-r-[4px]" : ""}`}
													style={{ flexGrow: s.value, flexBasis: 0, background: s.color }}
												/>
											))}
										</span>
									)}
								</div>
								<span className="w-[3rem] shrink-0 text-right font-mono text-[0.8125rem] text-ink tabular-nums">{r.total ?? formatInteger(sum)}</span>
							</div>
						</li>
					);
				})}
			</ul>
			<TipLayer tip={tip} />
		</>
	);
}
